import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import AuthModal from '../../../components/AuthModal';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const { user, isAuthenticated, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setShowProfileMenu(false);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/daa4d1286c7e40add692fc5e2a0b9f5f/593e1e03602cb23954036177d0cdac2a.png" 
                alt="Roseville.org" 
                className="h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className="text-gray-700 hover:text-teal-600 font-medium transition-colors whitespace-nowrap">
                Home
              </Link>
              <Link to="/directory" className="text-gray-700 hover:text-teal-600 font-medium transition-colors whitespace-nowrap">
                Directory
              </Link>
              <Link to="/calendar" className="text-gray-700 hover:text-teal-600 font-medium transition-colors whitespace-nowrap">
                Events
              </Link>
              <Link to="/deals" className="text-gray-700 hover:text-teal-600 font-medium transition-colors whitespace-nowrap">
                Deals
              </Link>
              <Link to="/leads" className="text-gray-700 hover:text-teal-600 font-medium transition-colors whitespace-nowrap">
                Get Quotes
              </Link>
              <Link to="/parks" className="text-gray-700 hover:text-teal-600 font-medium transition-colors whitespace-nowrap">
                Parks
              </Link>
              <Link to="/emergency" className="text-gray-700 hover:text-teal-600 font-medium transition-colors whitespace-nowrap">
                Emergency
              </Link>
              <Link to="/blog" className="text-gray-700 hover:text-teal-600 font-medium transition-colors whitespace-nowrap">
                Blog
              </Link>
            </div>

            {/* Auth Section */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isScrolled 
                      ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <i className="ri-user-line text-lg"></i>
                  <span className="font-medium whitespace-nowrap">My Account</span>
                  <i className={`ri-arrow-down-s-line transition-transform ${showProfileMenu ? 'rotate-180' : ''}`}></i>
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 border border-gray-100">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.user_metadata?.full_name || 'User'}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                    <Link
                      to="/favorites"
                      className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <i className="ri-heart-line text-lg"></i>
                      <span className="whitespace-nowrap">My Favorites</span>
                    </Link>
                    <Link
                      to="/lead-dashboard"
                      className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <i className="ri-customer-service-2-line text-lg"></i>
                      <span className="whitespace-nowrap">Lead Dashboard</span>
                    </Link>
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <i className="ri-settings-line text-lg"></i>
                      <span className="whitespace-nowrap">Settings</span>
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <i className="ri-logout-box-line text-lg"></i>
                      <span className="whitespace-nowrap">Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className={`px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  isScrolled 
                    ? 'bg-teal-600 text-white hover:bg-teal-700' 
                    : 'bg-white text-teal-600 hover:bg-gray-100'
                }`}
              >
                Sign In
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
            <div className="flex flex-col p-4 space-y-3">
              <Link
                to="/"
                className="text-gray-700 hover:text-teal-600 font-medium py-2 transition-colors whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/directory"
                className="text-gray-700 hover:text-teal-600 font-medium py-2 transition-colors whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Directory
              </Link>
              <Link
                to="/calendar"
                className="text-gray-700 hover:text-teal-600 font-medium py-2 transition-colors whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Events
              </Link>
              <Link
                to="/deals"
                className="text-gray-700 hover:text-teal-600 font-medium py-2 transition-colors whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Deals
              </Link>
              <Link
                to="/leads"
                className="text-gray-700 hover:text-teal-600 font-medium py-2 transition-colors whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Get Quotes
              </Link>
              <Link
                to="/parks"
                className="text-gray-700 hover:text-teal-600 font-medium py-2 transition-colors whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Parks
              </Link>
              <Link
                to="/emergency"
                className="text-gray-700 hover:text-teal-600 font-medium py-2 transition-colors whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Emergency
              </Link>
              <Link
                to="/blog"
                className="text-gray-700 hover:text-teal-600 font-medium py-2 transition-colors whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Blog
              </Link>

              {isAuthenticated ? (
                <>
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-900 mb-1">{user?.user_metadata?.full_name || 'User'}</p>
                    <p className="text-xs text-gray-500 mb-3">{user?.email}</p>
                  </div>
                  <Link
                    to="/favorites"
                    className="flex items-center gap-2 py-2 text-gray-700 hover:text-teal-600 font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-heart-line"></i>
                    My Favorites
                  </Link>
                  <Link
                    to="/lead-dashboard"
                    className="flex items-center gap-2 py-2 text-gray-700 hover:text-teal-600 font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-customer-service-2-line"></i>
                    Lead Dashboard
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 py-2 text-gray-700 hover:text-teal-600 font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-settings-line"></i>
                    Settings
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 py-2 text-red-600 hover:text-red-700 font-medium"
                  >
                    <i className="ri-logout-box-line"></i>
                    Sign Out
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setShowAuthModal(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-teal-600 text-white py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => setShowAuthModal(false)}
      />
    </>
  );
}